import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import { TenantInvoice, TenantInvoiceItem, TenantInvoicesResponse } from '../types/invoice'

const normalizeInvoiceItem = (raw: unknown): TenantInvoiceItem => {
    const item = raw as Record<string, unknown>
    return {
        id: Number(item.id),
        location: String(item.location ?? ''),
        service: String(item.service ?? item.service_name ?? ''),
        total_price: String(item.total_price ?? item.price ?? '0'),
    }
}

const normalizeInvoice = (raw: unknown): TenantInvoice => {
    const invoice = raw as Record<string, unknown>
    const rawItems =
        invoice.items ?? invoice.invoice_items ?? invoice.line_items ?? []

    return {
        id: Number(invoice.id),
        created_date: String(invoice.created_date ?? invoice.created_at ?? ''),
        invoice: (invoice.invoice ?? invoice.invoice_url ?? null) as string | null,
        service: (invoice.service ?? null) as TenantInvoice['service'],
        total_cost: String(invoice.total_cost ?? invoice.total_amount ?? '0'),
        items: Array.isArray(rawItems)
            ? rawItems.map(normalizeInvoiceItem)
            : [],
    }
}

const normalizeTenantInvoicesResponse = (
    data: unknown,
): TenantInvoicesResponse => {
    if (Array.isArray(data)) {
        const results = data.map(normalizeInvoice)
        return { count: results.length, results }
    }

    if (data && typeof data === 'object') {
        const payload = data as Record<string, unknown>

        if (payload.data && typeof payload.data === 'object') {
            return normalizeTenantInvoicesResponse(payload.data)
        }

        const results = payload.results ?? payload.invoices

        if (Array.isArray(results)) {
            const normalizedResults = results.map(normalizeInvoice)
            return {
                count:
                    typeof payload.count === 'number'
                        ? payload.count
                        : normalizedResults.length,
                results: normalizedResults,
            }
        }
    }

    return { count: 0, results: [] }
}

export const useTenantInvoices = (
    leaseId: number | null | undefined,
) => {
    const [isLoading, setIsLoading] = useState(false)
    const [invoices, setInvoices] = useState<TenantInvoicesResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    const getTenantInvoices = useCallback(async () => {
        if (leaseId == null) {
            setInvoices(null)
            setError(null)
            return null
        }
        setIsLoading(true)
        setError(null)
        try {
            const res = await axiosInstance.get(
                'invoice/tenant-invoices/',
                { params: { lease_id: leaseId } },
            )
            const normalized = normalizeTenantInvoicesResponse(res.data)
            setInvoices(normalized)
            setIsLoading(false)
            return normalized
        } catch (e) {
            toast.error('Error loading invoices')
            setError('Error loading invoices')
            setIsLoading(false)
            return Promise.reject(e)
        }
    }, [leaseId])

    return {
        invoices,
        isLoading,
        error,
        getTenantInvoices,
    }
}
